/**
 * Performance Utilities
 * Utilitários para otimização de performance geral
 */

class PerformanceUtils {
    /**
     * Executar função com throttle
     */
    static throttle(func, delay) {
        let lastCall = 0;
        return (...args) => {
            const now = Date.now();
            if (now - lastCall >= delay) {
                lastCall = now;
                return func(...args);
            }
        };
    }

    /**
     * Executar função com debounce
     */
    static debounce(func, delay) {
        let timeoutId = null;
        return (...args) => {
            clearTimeout(timeoutId);
            timeoutId = setTimeout(() => {
                func(...args);
            }, delay);
        };
    }

    /**
     * Executar código quando idle (quando navegador está ocioso)
     */
    static onIdle(callback, timeout = 2000) {
        if ('requestIdleCallback' in window) {
            requestIdleCallback(callback, { timeout });
        } else {
            setTimeout(callback, timeout);
        }
    }

    /**
     * Carregar script de forma assíncrona
     */
    static loadScript(src, options = {}) {
        return new Promise((resolve, reject) => {
            const script = document.createElement('script');
            script.src = src;
            script.async = true;

            if (options.defer) script.defer = true;
            if (options.type) script.type = options.type;
            if (options.id) script.id = options.id;

            script.onload = () => {
                resolve(script);
            };

            script.onerror = () => {
                reject(new Error(`Falha ao carregar script: ${src}`));
            };

            document.body.appendChild(script);
        });
    }

    /**
     * Carregar múltiplos scripts em paralelo
     */
    static loadScripts(srcArray) {
        return Promise.all(srcArray.map(src => this.loadScript(src)));
    }

    /**
     * Executar código após Network Idle
     */
    static onNetworkIdle(callback) {
        if ('PerformanceObserver' in window) {
            const observer = new PerformanceObserver(() => {
                // Esperar 2s sem requisições de rede
                clearTimeout(this.networkIdleTimeout);
                this.networkIdleTimeout = setTimeout(callback, 2000);
            });

            observer.observe({ entryTypes: ['resource'] });
        } else {
            setTimeout(callback, 5000); // Fallback
        }
    }

    /**
     * Medir performance de função
     */
    static measurePerformance(functionName, func) {
        return (...args) => {
            const start = performance.now();
            const result = func(...args);
            const end = performance.now();

            console.log(`⏱️ ${functionName}: ${(end - start).toFixed(2)}ms`);
            return result;
        };
    }

    /**
     * Report Web Vitals
     */
    static reportWebVitals(callback) {
        // Largest Contentful Paint
        if ('PerformanceObserver' in window) {
            try {
                const observer = new PerformanceObserver((entryList) => {
                    const entries = entryList.getEntries();
                    const lastEntry = entries[entries.length - 1];
                    callback({
                        name: 'LCP',
                        value: lastEntry.renderTime || lastEntry.loadTime
                    });
                });

                observer.observe({ entryTypes: ['largest-contentful-paint'] });
            } catch (e) {
                // Ignored
            }

            // First Input Delay (Interaction to Next Paint in newer browsers)
            try {
                const observer2 = new PerformanceObserver((entryList) => {
                    const entries = entryList.getEntries();
                    if (entries.length > 0) {
                        callback({
                            name: 'FID',
                            value: entries[0].processingDuration
                        });
                    }
                });

                observer2.observe({ entryTypes: ['first-input'] });
            } catch (e) {
                // Ignored
            }
        }

        // Cumulative Layout Shift
        let clsValue = 0;
        try {
            const observer3 = new PerformanceObserver((entryList) => {
                for (const entry of entryList.getEntries()) {
                    if (!entry.hadRecentInput) {
                        clsValue += entry.value;
                        callback({
                            name: 'CLS',
                            value: clsValue
                        });
                    }
                }
            });

            observer3.observe({ type: 'layout-shift', buffered: true });
        } catch (e) {
            // Ignored
        }
    }

    /**
     * Prefetch recurso
     */
    static prefetchResource(url, type = 'prefetch') {
        const link = document.createElement('link');
        link.rel = type; // 'prefetch', 'preload', 'preconnect'
        link.href = url;

        if (type === 'preload') {
            link.as = 'script'; // Pode ser ajustado
        }

        document.head.appendChild(link);
    }

    /**
     * Monitorar memória (Chrome)
     */
    static monitorMemory() {
        if ('memory' in performance) {
            return {
                usedMemory: performance.memory.usedJSHeapSize,
                totalMemory: performance.memory.totalJSHeapSize,
                limit: performance.memory.jsHeapSizeLimit
            };
        }
        return null;
    }

    /**
     * Criar AbortController com timeout automático
     */
    static createAbortController(timeout = 30000) {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), timeout);

        return {
            controller,
            clearTimeout: () => clearTimeout(timeoutId)
        };
    }

    /**
     * Simular conexão lenta (para testes)
     */
    static simulateSlowConnection(enabled = true) {
        if (enabled && 'chrome' in window) {
            console.log('💾 Simulando conexão lenta em DevTools');
        }
    }

    /**
     * Gerar relatório de performance
     */
    static generatePerformanceReport() {
        const navigation = performance.getEntriesByType('navigation')[0];
        const paint = performance.getEntriesByType('paint');

        return {
            dns: navigation.domainLookupEnd - navigation.domainLookupStart,
            tcp: navigation.connectEnd - navigation.connectStart,
            ttfb: navigation.responseStart - navigation.requestStart,
            download: navigation.responseEnd - navigation.responseStart,
            dom: navigation.domInteractive - navigation.domLoading,
            load: navigation.loadEventEnd - navigation.loadEventStart,
            fcp: paint.find(p => p.name === 'first-contentful-paint')?.startTime,
            total: navigation.loadEventEnd - navigation.fetchStart
        };
    }
}

// Disponibilizar globalmente
window.perfUtils = PerformanceUtils;
